import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { LookupsService } from '@zayan-erp/shared';
import * as SalesActions from './sales.actions';
import { selectProjectLotsState } from './sales.selectors';
import { AgencyRealtorService, SalesService } from '@zayan-erp/sales';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class SalesEffects {
  #actions$ = inject(Actions);
  #store = inject(Store);
  #lookupsService = inject(LookupsService);
  #agencyService = inject(AgencyRealtorService);
  #salesService = inject(SalesService);

  salesOfficeId = toSignal(this.#salesService.saleOfficeId$, { initialValue: null });
  loadSalesOffices$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadSalesOffices),
      switchMap(() =>
        this.#lookupsService
          .readSalesOffices()
          .pipe(map(salesOffices => SalesActions.loadSalesOfficesSuccess({ salesOffices })))
      )
    )
  );

  loadSalesOfficesPublic$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadSalesOfficesPublic),
      switchMap(() =>
        this.#lookupsService
          .readSalesOfficesPublic()
          .pipe(map(salesOffices => SalesActions.loadSalesOfficesPublicSuccess({ salesOffices })))
      )
    )
  );

  loadAgencies$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadAgencies),
      switchMap(() =>
        this.#agencyService.getAgencies().pipe(
          map((res: any) => {
            const raw: any[] = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
            return raw.map((a: any) => ({
              id: a.id ?? a.agencyId ?? a.AgencyId ?? a.Id,
              firstLabel: a.name ?? a.agencyName ?? a.Name ?? a.AgencyName ?? a.code ?? a.agencyCode ?? ''
            }));
          }),
          map(agencies => SalesActions.loadAgenciesSuccess({ agencies }))
        )
      )
    )
  );

  loadSalesAgents$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadSalesAgents),
      switchMap(() => {
        // Get salesOfficeId from the service
        const salesOfficeId = toSignal(this.#salesService.saleOfficeId$)() ?? null;
        // Use the new endpoint if salesOfficeId is available, otherwise fall back to the old endpoint
        const usersObservable = salesOfficeId
          ? this.#salesService.readSalesUsersBySalesOffice(salesOfficeId)
          : this.#salesService.readSalesUsers();

        return usersObservable.pipe(
          map(users =>
            users.map(u => ({
              id: u.id?.toString?.() ?? '',
              firstLabel: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
              secondLabel: u.userRoles?.[0]?.name ?? ''
            }))
          ),
          map(salesAgents => SalesActions.loadSalesAgentsSuccess({ salesAgents }))
        );
      })
    )
  );

  loadAgencyRealtors$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadAgencyRealtors),
      switchMap(() => {
        // Get salesOfficeId from the service
        // const salesOfficeId = this.salesOfficeId();

        return (
          this.#agencyService
            // .getAgencyRealtors({ pageNumber: 1, pageSize: 1000, salesOfficeId: salesOfficeId ?? undefined })
            .getAgencyRealtors({ pageNumber: 1, pageSize: 1000 })
            .pipe(
              map((res: any) =>
                (res.items || []).map((r: any) => ({
                  id: r.id || '',
                  firstLabel: `${r.firstName || ''} ${r.lastName || ''}`.trim(),
                  secondLabel: r.email || r.agency?.name || ''
                }))
              ),
              map(agencyRealtors => SalesActions.loadAgencyRealtorsSuccess({ agencyRealtors }))
            )
        );
      })
    )
  );

  loadProjectLots$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(SalesActions.loadSalesProjectLots),
      withLatestFrom(this.#store.select(selectProjectLotsState)),
      filter(([action, projectLots]) => !(projectLots.loaded && projectLots.projectId === action.projectId)),
      switchMap(([action]) =>
        this.#lookupsService.readLookupProjectLots(action.projectId, false, false, false).pipe(
          map(result =>
            SalesActions.loadSalesProjectLotsSuccess({
              projectId: action.projectId,
              lots: (result[action.projectId] ?? []).map(lot => ({
                id: String(lot.id),
                lotNo: lot.firstLabel ?? ''
              }))
            })
          ),
          catchError((error: unknown) =>
            error instanceof HttpErrorResponse && error.status === 0
              ? EMPTY
              : of(SalesActions.loadSalesProjectLotsFailure({ projectId: action.projectId }))
          )
        )
      )
    )
  );
}
